import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { useWebSocket, useUser } from './WebSocketContext';
import '../styles/Sidebar.css';
import { useTranslation } from 'react-i18next';
import '../i18n';
import CertificationExpiredOverlay from './CertificationExpiredOverlay';
import SoftwareOutdatedOverlay from './SoftwareOutdatedOverlay';
import { useSnackbar } from '../Utils/SnackBarContext';
import SidebarDrawer from './SidebarDrawer';
import SidebarToggleButton from './SidebarToggleButton';
import SidebarLanguageMenu from './SidebarLanguageMenu';
import CaseContextMenu from './CaseContextMenu';
import CaseRenameDialog from './CaseRenameDialog';
import CaseDuplicateDialog from './CaseDuplicateDialog';
import SaveTemplateDialog from './SaveTemplateDialog';
import CollectionCreateDialog from './CollectionCreateDialog';
import AddToCollectionDialog from './AddToCollectionDialog';
import CaseDeleteDialog from './CaseDeleteDialog';
import { useCaseDelete } from '../hooks/useCaseDelete';
import { useFolderRestrictions } from './useFolderRestrictions';
import StorageBrowser from './StorageBrowser';

const Sidebar = ({ currentCaseId }) => {
  const { subscribeToMessages, sendMessage, logout, isUserLoggedIn, isConnected } = useWebSocket();
  const { isReadOnly } = useUser();
  const { openSnackbar } = useSnackbar();
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(currentCaseId);
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [newEventsCount, setNewEventsCount] = useState(0);
  const [credits, setCredits] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [certOverlayOpen, setCertOverlayOpen] = useState(false);
  const [softwareOutdatedOverlayOpen, setSoftwareOutdatedOverlayOpen] = useState(false);
  const [downloadLink, setDownloadLink] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [storageStatus, setStorageStatus] = useState({
    isLoading: true,
    totalBytes: 0,
    usedBytes: 0,
    freeBytes: 0,
    usedPercent: 0,
    freePercent: 0,
    restrictToUserFolder: false,
    error: ''
  });
  const [storageBrowserPath, setStorageBrowserPath] = useState('');
  const [storageDisplayPath, setStorageDisplayPath] = useState('/');
  const [storageFolders, setStorageFolders] = useState([]);
  const [storageFiles, setStorageFiles] = useState([]);
  const [storageBrowserLoading, setStorageBrowserLoading] = useState(false);
  const [storageBrowserError, setStorageBrowserError] = useState('');
  const [storageDialogOpen, setStorageDialogOpen] = useState(false);
  const [storageDownloadJob, setStorageDownloadJob] = useState({
    active: false,
    filename: '',
    totalChunks: 0,
    receivedChunks: 0
  });
  const storageDownloadChunksRef = useRef([]);

  // Menu states
  const [languageMenuAnchorEl, setLanguageMenuAnchorEl] = useState(null);
  const [caseMenuAnchorEl, setCaseMenuAnchorEl] = useState(null);
  const [selectedCaseForMenu, setSelectedCaseForMenu] = useState(null);

  // Dialog states
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingCase, setRenamingCase] = useState(null);
  const [renamingCaseId, setRenamingCaseId] = useState(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyingCase, setCopyingCase] = useState(null);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [templateCase, setTemplateCase] = useState(null);

  // Collection states
  const [collections, setCollections] = useState([]);
  const [collectionCreateDialogOpen, setCollectionCreateDialogOpen] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [addToCollectionDialogOpen, setAddToCollectionDialogOpen] = useState(false);
  const [caseToAddToCollection, setCaseToAddToCollection] = useState(null);

  const itemsPerPage = 15;
  const navigate = useNavigate();
  const location = useLocation();

  const activeCaseId = useMemo(() => {
    if (currentCaseId) return currentCaseId;
    const match = matchPath('/case/:caseid', location.pathname);
    const id = match ? parseInt(match.params.caseid, 10) : null;
    console.log('Sidebar activeCaseId derived:', id, 'from path:', location.pathname);
    return id;
  }, [currentCaseId, location.pathname]);

  // Use case delete hook for DRY approach
  const {
    deleteDialogOpen,
    caseToDelete,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
    isDeleting,
    handleDeleteResponse
  } = useCaseDelete({
    sendMessage,
    isConnected,
    onDeleteSuccess: (deletedCase) => {
      // Refresh the cases list after successful delete
      debouncedGetListOfCases(searchQuery, currentPage);
    },
    navigateOnDelete: true,
    currentCaseId: activeCaseId
  });

  // Theme colors
  const primaryColor = '#5757b0';
  const hoverColor = '#7777d0';
  const selectedColor = '#4545a0';
  const lightPurple = '#f0f0ff';

  // Sidebar handlers
  const toggleDrawer = () => {
    setIsOpen(prevState => !prevState);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Language menu handlers
  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchorEl(null);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    console.log('Language changed to:', lng);
  };

  // Case menu handlers
  const handleCaseMenuOpen = (event, caseData) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('Opening case menu for:', caseData);
    setCaseMenuAnchorEl(event.currentTarget);
    setSelectedCaseForMenu(caseData);
  };

  const handleCaseMenuClose = () => {
    console.log('Closing case menu');
    setCaseMenuAnchorEl(null);
    setSelectedCaseForMenu(null);
  };

  const handleRenameClick = () => {
    console.log('Rename clicked for case:', selectedCaseForMenu);
    if (selectedCaseForMenu) {
      setRenamingCase(selectedCaseForMenu);
      setRenameDialogOpen(true);
    }
  };

  const handleRenameDialogClose = () => {
    setRenameDialogOpen(false);
    setRenamingCase(null);
  };

  const handleCopyClick = () => {
    console.log('Copy clicked for case:', selectedCaseForMenu);
    if (selectedCaseForMenu) {
      setCopyingCase(selectedCaseForMenu);
      setCopyDialogOpen(true);
    }
  };

  const handleCopyDialogClose = () => {
    setCopyDialogOpen(false);
    setCopyingCase(null);
  };

  const handleSaveAsTemplateClick = () => {
    console.log('Save as template clicked for case:', selectedCaseForMenu);
    if (selectedCaseForMenu) {
      setTemplateCase(selectedCaseForMenu);
      setSaveTemplateDialogOpen(true);
    }
  };

  const handleSaveTemplateDialogClose = () => {
    setSaveTemplateDialogOpen(false);
    setTemplateCase(null);
  };

  // Collection handlers
  const handleCollectionCreateDialogOpen = () => {
    setCollectionCreateDialogOpen(true);
  };

  const handleCollectionCreateDialogClose = () => {
    setCollectionCreateDialogOpen(false);
  };

  const handleCollectionChange = (collectionId) => {
    setSelectedCollection(collectionId);
    console.log('Collection changed to:', collectionId);
    // Reset to first page when changing collection
    setCurrentPage(1);
  };

  const handleCollectionCreate = (collectionName) => {
    if (!sendMessage || !isConnected) {
      console.log('WebSocket not available or not connected');
      openSnackbar(t('collection.create_error_connection') || 'Connection error. Please try again.', 3000, 'error');
      return;
    }

    try {
      const message = {
        command: 'createCollection',
        title: collectionName
      };

      console.log('Sending WebSocket createCollection message:', message);
      sendMessage(message);
      console.log('Create collection message sent, waiting for server response');
    } catch (error) {
      console.error('Error creating collection:', error);
      openSnackbar(t('collection.create_error') || 'Failed to create collection', 3000, 'error');
    }
  };

  const getCollections = useCallback(() => {
    if (sendMessage && isConnected) {
      console.log('Fetching collections...');
      setCollectionsLoading(true);
      sendMessage({ command: 'getAllCollections' });
    }
  }, [sendMessage, isConnected]);

  const handleAddToCollection = (caseItem) => {
    setCaseToAddToCollection(caseItem);
    setAddToCollectionDialogOpen(true);
  };

  const handleAddToCollectionConfirm = (collectionId) => {
    if (!caseToAddToCollection || !collectionId || !sendMessage || !isConnected) {
      console.log('Missing data for adding to collection');
      return;
    }

    try {
      const message = {
        command: 'addProjectToCollection',
        project_id: caseToAddToCollection.id,
        collection_id: collectionId
      };

      console.log('Sending addProjectToCollection message:', message);
      sendMessage(message);

      // Close dialog and reset state
      setAddToCollectionDialogOpen(false);
      setCaseToAddToCollection(null);

      openSnackbar(t('collection.case_added') || 'Case added to collection successfully', 3000, 'success');
    } catch (error) {
      console.error('Error adding case to collection:', error);
      openSnackbar(t('collection.add_error') || 'Failed to add case to collection', 3000, 'error');
    }
  };

  const handleAddToCollectionCancel = () => {
    setAddToCollectionDialogOpen(false);
    setCaseToAddToCollection(null);
  };

  const handleDeleteClick = () => {
    if (selectedCaseForMenu) {
      openDeleteDialog(selectedCaseForMenu);
    }
  };

  const handleSaveTemplateSubmit = ({ templateName, templateDescription, version, caseId }) => {
    console.log('Save template submit called with:', { templateName, templateDescription, version, caseId });

    if (!templateCase || !templateName.trim()) {
      console.log('Missing template data');
      return;
    }

    if (!sendMessage || !isConnected) {
      console.log('WebSocket not available or not connected');
      openSnackbar(t('template.save_error_connection') || 'Connection error. Please try again.', 3000, 'error');
      return;
    }

    try {
      const message = {
        command: 'saveTemplate',
        template_title: templateName,
        template_description: templateDescription,
        version: version,
        case_id: templateCase.id
      };

      console.log('Sending WebSocket saveTemplate message:', message);

      sendMessage(message);
      console.log('Save template message sent, waiting for server response');
      openSnackbar(t('template.save_success') || 'Template saved successfully', 3000, 'success');
    } catch (error) {
      console.error('Error saving template:', error);
      openSnackbar(t('template.save_error') || 'Failed to save template', 3000, 'error');
    }
  };

  const handleCopySubmit = (newTitle, collectionId) => {
    console.log('Copy submit called with:', { copyingCase, newTitle, collectionId });

    if (!copyingCase) {
      console.log('No case selected for copy');
      return;
    }

    if (!sendMessage || !isConnected) {
      console.log('WebSocket not available or not connected');
      openSnackbar(t('sidebar.copy_case_error_connection') || 'Connection error. Please try again.', 3000, 'error');
      return;
    }

    try {
      const message = {
        command: 'copyProject',
        caseid: copyingCase.id,
        title: newTitle,
        collection_id: collectionId
      };

      console.log('Sending WebSocket copy message:', message);
      sendMessage(message);
      console.log('Copy message sent, waiting for server response');
      // Note: Success message and refresh will be handled in the message subscription
    } catch (error) {
      console.error('Error copying case:', error);
      openSnackbar(t('sidebar.copy_case_error') || 'Failed to duplicate case', 3000, 'error');
    }
  };

  const handleRenameSubmit = (newTitle) => {
    console.log('Rename submit called with:', { renamingCase, newTitle });

    if (!renamingCase) {
      console.log('No case selected for rename');
      return;
    }

    if (!sendMessage || !isConnected) {
      console.log('WebSocket not available or not connected');
      openSnackbar(t('sidebar.rename_error_connection') || 'Connection error. Please try again.', 3000, 'error');
      return;
    }

    try {
      const message = {
        command: 'changetitle',
        caseid: renamingCase.id,
        title: newTitle
      };

      console.log('Sending WebSocket message:', message);

      // Store the case ID being renamed so we can update it when the server responds
      setRenamingCaseId(renamingCase.id);

      sendMessage(message); console.log('Rename message sent, waiting for server response');
      openSnackbar(t('sidebar.rename_success') || 'Case renamed successfully', 3000, 'success');
    } catch (error) {
      console.error('Error renaming case:', error);
      openSnackbar(t('sidebar.rename_error') || 'Failed to rename case', 3000, 'error');
    }
  };

  const getListOfCases = useCallback((query = '') => {
    console.log('getListOfCases called with query:', query, 'page:', currentPage, 'collection:', selectedCollection, 'isConnected:', isConnected);
    if (sendMessage && isConnected) {
      console.log('Sending searchcases command...');
      sendMessage({
        command: 'searchcases',
        query,
        page: currentPage,
        collection_id: selectedCollection
      });
    } else {
      console.error('WebSocket connection is not available or not connected yet.');
    }
  }, [sendMessage, currentPage, selectedCollection, isConnected]);

  // Reload cases when collection changes
  useEffect(() => {
    if (isConnected) {
      console.log('Collection changed, reloading cases...');
      getListOfCases(searchQuery);
    }
  }, [selectedCollection, isConnected, getListOfCases, searchQuery]);

  // Retry function for failed connections
  const retryConnection = useCallback(() => {
    console.log('Retrying connection...');
    setConnectionError(false);
    setIsLoading(true);
    if (sendMessage && isConnected) {
      getListOfCases();
      sendMessage({ command: 'configuration_check' });
    }
  }, [sendMessage, isConnected, getListOfCases]);

  const debounceTimeoutRef = useRef(null);
  const storageRefreshIntervalRef = useRef(null);

  const debouncedGetListOfCases = useCallback((query, page) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (sendMessage && isConnected) {
        sendMessage({
          command: 'searchcases',
          query,
          page,
          collection_id: selectedCollection
        });
      } else {
        console.error('WebSocket connection is not available or not connected yet.');
      }
    }, 500);
  }, [sendMessage, isConnected, selectedCollection]);

  const requestStorageStatus = useCallback(() => {
    if (!sendMessage || !isConnected) {
      return;
    }
    setStorageStatus((prev) => ({
      ...prev,
      isLoading: true,
      error: ''
    }));
    sendMessage({ command: 'get_storage_status' });
  }, [sendMessage, isConnected]);

  const { storageInfo } = useWebSocket();
  const { isAdmin, isEdit } = useUser();
  const canManageStorage = useMemo(() => isAdmin || isEdit, [isAdmin, isEdit]);
  const { restrictToUserFolder, allowedRoots } = useFolderRestrictions(currentCaseId);
  const storageConfigRoot = useMemo(() => storageStatus?.path || storageInfo?.path || '', [storageStatus, storageInfo]);
  const storageAllowedRoot = useMemo(() => {
    if (!canManageStorage) return '';
    const restrictionFlag = restrictToUserFolder || storageInfo?.restrictToUserFolder || storageStatus?.restrictToUserFolder;
    if (!restrictionFlag) return '';
    if (Array.isArray(allowedRoots) && allowedRoots.length > 0) {
      return allowedRoots[0];
    }
    return storageConfigRoot || '';
  }, [allowedRoots, restrictToUserFolder, storageConfigRoot, storageInfo, canManageStorage, storageStatus]);
  const storageRestrictionEnabled = useMemo(() => {
    const restrictionFlag = restrictToUserFolder || storageInfo?.restrictToUserFolder || storageStatus?.restrictToUserFolder;
    return Boolean(canManageStorage && restrictionFlag);
  }, [restrictToUserFolder, storageInfo, canManageStorage, storageStatus]);
  const isRestrictedFolderMode = useMemo(
    () => Boolean(restrictToUserFolder || storageInfo?.restrictToUserFolder || storageStatus?.restrictToUserFolder),
    [restrictToUserFolder, storageInfo, storageStatus]
  );

  const normalizePathForCompare = useCallback((value) => {
    if (!value) return '';
    return value.replace(/\\/g, '/').replace(/\/+/g, '/').toLowerCase().replace(/\/$/, '');
  }, []);

  const normalizedAllowedRoot = useMemo(() => normalizePathForCompare(storageAllowedRoot), [normalizePathForCompare, storageAllowedRoot]);

  const isPathWithinAllowed = useCallback((path) => {
    if (!storageRestrictionEnabled || !normalizedAllowedRoot) return false;
    const normalized = normalizePathForCompare(path);
    if (!normalized) return false;
    return normalized === normalizedAllowedRoot || normalized.startsWith(`${normalizedAllowedRoot}/`);
  }, [normalizedAllowedRoot, normalizePathForCompare, storageRestrictionEnabled]);

  const resetStorageBrowser = useCallback(() => {
    setStorageBrowserPath('');
    setStorageDisplayPath('/');
    setStorageFolders([]);
    setStorageFiles([]);
    setStorageBrowserLoading(false);
    setStorageBrowserError('');
  }, []);

  const requestStorageDirectory = useCallback((targetPath) => {
    if (!storageRestrictionEnabled || !storageAllowedRoot) {
      resetStorageBrowser();
      return;
    }
    if (!sendMessage || !isConnected) {
      setStorageBrowserError(t('sidebar.storage_connect_error', 'Unable to load storage while offline'));
      return;
    }
    const nextPath = targetPath || storageAllowedRoot;
    if (!nextPath) {
      // Try to fetch status to get a root path
      sendMessage({ command: 'get_storage_status' });
      return;
    }
    setStorageBrowserLoading(true);
    setStorageBrowserError('');
    sendMessage({ command: 'listDirectory', path: nextPath });
    sendMessage({ command: 'listFilesInDirectory', path: nextPath });
  }, [isConnected, storageRestrictionEnabled, resetStorageBrowser, sendMessage, storageAllowedRoot, t]);

  const handleStorageFolderClick = useCallback((folderName) => {
    if (!folderName || !storageAllowedRoot) return;
    const basePath = storageBrowserPath || storageAllowedRoot;
    const separator = basePath.includes('\\') ? '\\' : '/';
    const trimmedBase = basePath.endsWith(separator) ? basePath.slice(0, -1) : basePath;
    const nextPath = `${trimmedBase}${separator}${folderName}`;
    if (!isPathWithinAllowed(nextPath)) {
      requestStorageDirectory(storageAllowedRoot);
      return;
    }
    requestStorageDirectory(nextPath);
  }, [isPathWithinAllowed, requestStorageDirectory, storageAllowedRoot, storageBrowserPath]);

  const handleStorageNavigateUp = useCallback(() => {
    if (!storageBrowserPath) return;
    const segments = storageBrowserPath.split(/[\\/]/).filter(Boolean);
    if (segments.length === 0) return;
    const isWindows = storageBrowserPath.includes('\\');
    const separator = isWindows ? '\\' : '/';
    const hasDrive = isWindows && /^[A-Za-z]:/.test(segments[0]);
    let drive = '';
    let parts = segments;
    if (hasDrive) {
      drive = segments[0];
      parts = segments.slice(1);
    }
    if (parts.length === 0) {
      setStorageBrowserError(t('sidebar.storage_root_stop', 'You are already at the top of your allowed folder'));
      return;
    }
    parts.pop();
    const parentPath = hasDrive
      ? (parts.length ? `${drive}${separator}${parts.join(separator)}` : drive)
      : `${separator}${parts.join(separator)}`;
    if (!isPathWithinAllowed(parentPath)) {
      requestStorageDirectory(storageAllowedRoot);
      return;
    }
    requestStorageDirectory(parentPath);
  }, [isPathWithinAllowed, requestStorageDirectory, storageBrowserPath, storageAllowedRoot]);

  const handleStorageRefresh = useCallback(() => {
    if (storageBrowserPath) {
      requestStorageDirectory(storageBrowserPath);
    } else if (storageAllowedRoot) {
      requestStorageDirectory(storageAllowedRoot);
    }
  }, [requestStorageDirectory, storageAllowedRoot, storageBrowserPath]);

  const openStorageDialog = useCallback(() => {
    if (storageRestrictionEnabled && !storageDialogOpen) {
      setStorageDialogOpen(true);
      if (!storageBrowserPath && storageAllowedRoot) {
        requestStorageDirectory(storageAllowedRoot);
      }
    }
  }, [storageAllowedRoot, storageBrowserPath, storageDialogOpen, storageRestrictionEnabled, requestStorageDirectory]);

  const closeStorageDialog = useCallback(() => {
    setStorageDialogOpen(false);
  }, []);

  const handleStorageFileDownload = useCallback((fileName) => {
    if (!storageRestrictionEnabled || !storageAllowedRoot || !fileName || !sendMessage || !isConnected) {
      return;
    }
    const basePath = storageBrowserPath || storageAllowedRoot;
    const separator = basePath.includes('\\') ? '\\' : '/';
    const trimmedBase = basePath.endsWith(separator) ? basePath.slice(0, -1) : basePath;
    const targetPath = `${trimmedBase}${separator}${fileName}`;

    storageDownloadChunksRef.current = [];
    setStorageDownloadJob({
      active: true,
      filename: fileName,
      totalChunks: 0,
      receivedChunks: 0
    });

    sendMessage({
      command: 'openfile',
      path: targetPath,
      download: true
    });
  }, [isConnected, sendMessage, storageAllowedRoot, storageBrowserPath, storageRestrictionEnabled]);

  const handleStorageFolderDownload = useCallback(() => {
    if (!storageRestrictionEnabled || !storageAllowedRoot || !sendMessage || !isConnected) {
      return;
    }
    const basePath = storageBrowserPath || storageAllowedRoot;
    
    setStorageDownloadJob({
      active: true,
      preparing: true,
      filename: '',
      totalChunks: 0,
      receivedChunks: 0
    });

    sendMessage({
      command: 'downloadStorageFolder',
      path: basePath
    });
  }, [isConnected, sendMessage, storageAllowedRoot, storageBrowserPath, storageRestrictionEnabled]);

  const assembleAndSaveFile = useCallback((filename, chunks) => {
    if (!chunks || chunks.length === 0) return;
    const blob = new Blob(chunks, { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleStorageCreateFolder = useCallback((name) => {
    if (!storageRestrictionEnabled) {
      openSnackbar(t('sidebar.storage_not_restricted', 'Storage actions are only available in restricted mode'), 3000, 'error');
      return;
    }
    if (!storageAllowedRoot) {
      openSnackbar(t('sidebar.storage_no_root', 'Storage location unavailable'), 3000, 'error');
      return;
    }
    if (!sendMessage || !isConnected) {
      openSnackbar(t('sidebar.storage_connect_error', 'Unable to load storage while offline'), 3000, 'error');
      return;
    }
    const folderName = (name || '').trim();
    if (!folderName) {
      openSnackbar(t('sidebar.storage_folder_name_required', 'Folder name is required'), 3000, 'error');
      return;
    }
    const basePath = storageBrowserPath || storageAllowedRoot;
    setStorageBrowserLoading(true);
    setStorageBrowserError('');
    sendMessage({ command: 'createFolder', path: basePath, name: folderName });
  }, [isConnected, openSnackbar, sendMessage, storageAllowedRoot, storageBrowserPath, storageRestrictionEnabled, t]);

  const handleStorageDelete = useCallback((targetName, targetType) => {
    if (!storageRestrictionEnabled) {
      openSnackbar(t('sidebar.storage_not_restricted', 'Storage actions are only available in restricted mode'), 3000, 'error');
      return;
    }
    if (!storageAllowedRoot) {
      openSnackbar(t('sidebar.storage_no_root', 'Storage location unavailable'), 3000, 'error');
      return;
    }
    if (!sendMessage || !isConnected) {
      openSnackbar(t('sidebar.storage_connect_error', 'Unable to load storage while offline'), 3000, 'error');
      return;
    }
    if (!targetName) return;
    const basePath = storageBrowserPath || storageAllowedRoot;
    const separator = basePath.includes('\\') ? '\\' : '/';
    const trimmedBase = basePath.endsWith(separator) ? basePath.slice(0, -1) : basePath;
    const targetPath = `${trimmedBase}${separator}${targetName}`;
    setStorageBrowserLoading(true);
    setStorageBrowserError('');
    sendMessage({ command: 'deletePath', path: targetPath, targetType });
  }, [isConnected, sendMessage, storageAllowedRoot, storageBrowserPath, storageRestrictionEnabled, t]);

  const handleStorageDeleteFile = useCallback((name) => handleStorageDelete(name, 'file'), [handleStorageDelete]);
  const handleStorageDeleteFolder = useCallback((name) => handleStorageDelete(name, 'folder'), [handleStorageDelete]);

  useEffect(() => {
    console.log('Sidebar effect triggered - sendMessage available:', !!sendMessage, 'isConnected:', isConnected);
    if (sendMessage && isConnected) {
      console.log('Getting list of cases...');
      getListOfCases();
      console.log('Getting list of collections...');
      getCollections();
      // Check configuration when WebSocket is connected
      console.log('Sending configuration check...');
      sendMessage({ command: 'configuration_check' });
      setIsLoading(false);
      setConnectionError(false);
    } else {
      console.log('sendMessage not available yet or WebSocket not connected');
      setIsLoading(true);

      // Set a timeout to show connection error if WebSocket doesn't connect within 10 seconds
      const timeout = setTimeout(() => {
        if (!isConnected) {
          setIsLoading(false);
          setConnectionError(true);
        }
      }, 10000);

      return () => clearTimeout(timeout);
    }
  }, [sendMessage, getListOfCases, getCollections, isConnected]);

  useEffect(() => {
    if (!isConnected) {
      if (storageRefreshIntervalRef.current) {
        clearInterval(storageRefreshIntervalRef.current);
        storageRefreshIntervalRef.current = null;
      }
      resetStorageBrowser();
      return;
    }

    requestStorageStatus();
    if (storageRefreshIntervalRef.current) {
      clearInterval(storageRefreshIntervalRef.current);
    }
    storageRefreshIntervalRef.current = setInterval(requestStorageStatus, 60000);

    return () => {
      if (storageRefreshIntervalRef.current) {
        clearInterval(storageRefreshIntervalRef.current);
        storageRefreshIntervalRef.current = null;
      }
    };
  }, [isConnected, requestStorageStatus, resetStorageBrowser]);

  useEffect(() => {
    if (storageRestrictionEnabled && storageAllowedRoot && isConnected) {
      requestStorageDirectory(storageAllowedRoot);
    } else {
      resetStorageBrowser();
    }
  }, [isConnected, requestStorageDirectory, resetStorageBrowser, storageRestrictionEnabled, storageAllowedRoot]);

  useEffect(() => {
    console.log('Setting up message subscription...');
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      // console.log('Sidebar received message:', parsedMessage.command, parsedMessage);

      if (parsedMessage.command === "certification") {
        console.log("Status:", parsedMessage.data.status);
        if (parsedMessage.data.status === "software_outdate") {
          setDownloadLink(parsedMessage.data.download || '#');
          setSoftwareOutdatedOverlayOpen(true);
        }
        if (parsedMessage.data.status === "certification_invalid") {
          console.log("open certification expired overlay");
          setCertOverlayOpen(true);
        }
      } else if (parsedMessage.command === "configuration_data") {
        // Check if a text model provider is selected (not just if token exists)
        const hasTextModel = parsedMessage.data && parsedMessage.data.modelTextHost && parsedMessage.data.modelTextHost.trim() !== '';
        if (!hasTextModel && !isReadOnly) {
          console.log("No text model selected. Redirecting to settings.");
          // Navigate to settings page if no model is selected
          // Don't redirect view_only users - they can't configure anyway
          navigate('/Settings');
        }

        // Update credits if available
        if (parsedMessage.data && parsedMessage.data.credits !== undefined) {
          setCredits(parsedMessage.data.credits);
        }
      } else if (parsedMessage.command === "storage_status") {
        const payload = parsedMessage.data || {};
        const restrictFlag = Boolean(payload.restrict_to_user_folder ?? payload.restrictToUserFolder);
        if (payload.status === "error") {
          setStorageStatus({
            isLoading: false,
            totalBytes: 0,
            usedBytes: 0,
            freeBytes: 0,
            usedPercent: 0,
            freePercent: 0,
            path: payload.path || '',
            restrictToUserFolder: restrictFlag,
            error: payload.message || t('sidebar.storage_error', 'Unable to load storage status')
          });
        } else {
          const totalBytes = Number(payload.total_bytes ?? payload.totalBytes ?? payload.total ?? 0);
          const usedBytes = Number(payload.used_bytes ?? payload.usedBytes ?? payload.used ?? 0);
          const freeBytes = Number(payload.free_bytes ?? payload.freeBytes ?? payload.free ?? (totalBytes - usedBytes));
          const usedPercentRaw = payload.used_percent ?? payload.usedPercent ?? (totalBytes ? (usedBytes / totalBytes) * 100 : 0);
          const freePercentRaw = payload.free_percent ?? payload.freePercent ?? (100 - usedPercentRaw);

          setStorageStatus({
            isLoading: false,
            totalBytes,
            usedBytes,
            freeBytes,
            usedPercent: Math.min(100, Math.max(0, Number(usedPercentRaw)) || 0),
            freePercent: Math.min(100, Math.max(0, Number(freePercentRaw)) || 0),
            path: payload.path || '',
            restrictToUserFolder: restrictFlag,
            error: ''
          });
        }
      } else if (parsedMessage.command === "listDirectory") {
        if (!storageRestrictionEnabled || !storageAllowedRoot) {
          return;
        }
        const data = parsedMessage.data || {};
        const incomingPath = data.currentPath || '';
        const displayPath = data.displayPath ?? incomingPath;
        const normalizedIncoming = normalizePathForCompare(incomingPath);
        if (normalizedAllowedRoot && normalizedIncoming && !normalizedIncoming.startsWith(normalizedAllowedRoot)) {
          return;
        }
        if (data.error) {
          setStorageBrowserError(data.error);
          setStorageFolders([]);
          setStorageFiles([]);
          setStorageBrowserLoading(false);
          return;
        }
        setStorageFolders(data.folders || []);
        setStorageBrowserPath(incomingPath || storageAllowedRoot);
        setStorageDisplayPath(displayPath || '/');
        setStorageBrowserLoading(false);
      } else if (parsedMessage.command === "listFilesInDirectory") {
        if (!storageRestrictionEnabled || !storageAllowedRoot) {
          return;
        }
        const data = parsedMessage.data || {};
        const incomingPath = data.currentPath || '';
        const normalizedIncoming = normalizePathForCompare(incomingPath);
        if (normalizedAllowedRoot && normalizedIncoming && !normalizedIncoming.startsWith(normalizedAllowedRoot)) {
          return;
        }
        if (data.error) {
          setStorageBrowserError(data.error);
          setStorageFiles([]);
          setStorageBrowserLoading(false);
          return;
        }
        if (data.displayPath) {
          setStorageDisplayPath(data.displayPath);
        }
        if (incomingPath) {
          setStorageBrowserPath(incomingPath);
        }
        const rawFiles = data.files || [];
        const normalizedFiles = rawFiles
          .map((file) => {
            if (typeof file === 'string') {
              return { name: file, createdAt: '' };
            }
            if (file && typeof file === 'object') {
              return {
                name: file.name || '',
                createdAt: file.created_at || file.createdAt || ''
              };
            }
            return null;
          })
          .filter((f) => f && f.name);
        setStorageFiles(normalizedFiles);
        setStorageBrowserLoading(false);
      } else if (parsedMessage.command === "createFolder") {
        const data = parsedMessage.data || parsedMessage;
        if (data.error) {
          setStorageBrowserLoading(false);
          setStorageBrowserError(data.error);
          openSnackbar(data.error, 3000, 'error');
        } else if (data.success) {
          openSnackbar(t('sidebar.storage_folder_created', 'Folder created'), 2000, 'success');
          requestStorageDirectory(storageBrowserPath || storageAllowedRoot);
        }
      } else if (parsedMessage.command === "deletePath") {
        const data = parsedMessage.data || parsedMessage;
        if (data.error) {
          setStorageBrowserLoading(false);
          setStorageBrowserError(data.error);
          openSnackbar(data.error, 3000, 'error');
        } else if (data.success) {
          openSnackbar(t('sidebar.storage_deleted', 'Deleted successfully'), 2000, 'success');
          requestStorageDirectory(storageBrowserPath || storageAllowedRoot);
        }
      } else if (parsedMessage.command === "downloadStorageFolder") {
        setStorageDownloadJob({
          active: false,
          preparing: false,
          filename: '',
          totalChunks: 0,
          receivedChunks: 0
        });
        
        if (parsedMessage.data.status === 'success') {
          // Trigger download
          const link = document.createElement('a');
          link.href = `data:application/zip;base64,${parsedMessage.data.data}`;
          link.download = parsedMessage.data.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          openSnackbar(t('sidebar.storage_download_success', 'Folder downloaded successfully'), 2000, 'success');
        } else {
          openSnackbar(parsedMessage.data.message || t('sidebar.storage_download_error', 'Error downloading folder'), 3000, 'error');
        }
      } else if (parsedMessage.command === "changetitle") {
        // Handle successful title change response from server
        console.log("Title changed successfully:", parsedMessage.data);
        // Update only the specific case in the existing list using the stored case ID
        const newTitle = parsedMessage.data;
        if (renamingCaseId) {
          setCases(prevCases =>
            prevCases.map(caseData =>
              caseData.id === renamingCaseId
                ? { ...caseData, title: newTitle }
                : caseData
            )
          );
          // Clear the renaming case ID
          setRenamingCaseId(null);
          console.log('Case title updated in list for case ID:', renamingCaseId);
        }
      } else if (parsedMessage.command === "copyProject") {
        // Handle copyProject response from server
        console.log("Copy project response:", parsedMessage);
        if (parsedMessage.status === "success" || parsedMessage.data?.status === "success") {
          openSnackbar(t('sidebar.copy_case_success') || 'Case duplicated successfully', 3000, 'success');
          // Refresh the cases list to show the new copied case
          debouncedGetListOfCases(searchQuery, currentPage);
        } else {
          const errorMsg = parsedMessage.message || parsedMessage.data?.message || t('sidebar.copy_case_error') || 'Failed to duplicate case';
          openSnackbar(errorMsg, 3000, 'error');
        }
      } else if (parsedMessage.command === "deletecase") {
        // Handle deletecase response from server using the hook
        console.log("Delete case response received:", parsedMessage);

        let success = false;
        if (typeof parsedMessage.data === 'string') {
          success = parsedMessage.data === "success";
        } else if (parsedMessage.data && parsedMessage.data.value) {
          success = parsedMessage.data.value === "success";
        } else if (parsedMessage.data && parsedMessage.data.status) {
          success = parsedMessage.data.status === "success";
        }

        console.log("Delete success status:", success, "caseToDelete:", caseToDelete);

        handleDeleteResponse(success, caseToDelete?.id);
        if (success) {
          // Refresh the cases list
          debouncedGetListOfCases(searchQuery, currentPage);
        }
      } else if (parsedMessage.command === "saveTemplate") {
        // Handle saveTemplate response from server
        console.log("Save template response:", parsedMessage);
        if (parsedMessage.status === "success") {
          openSnackbar(parsedMessage.message || t('template.save_success') || 'Template saved successfully', 3000, 'success');
        } else {
          openSnackbar(parsedMessage.message || t('template.save_error') || 'Failed to save template', 3000, 'error');
        }
      } else if (parsedMessage.command === "createCollection") {
        // Handle createCollection response from server
        console.log("Create collection response:", parsedMessage);
        if (parsedMessage.data && parsedMessage.data.status === "success") {
          openSnackbar(parsedMessage.data.message || t('collection.create_success') || 'Collection created successfully', 3000, 'success');
          // Add the new collection to the list
          if (parsedMessage.data.collection) {
            setCollections(prevCollections => [...prevCollections, parsedMessage.data.collection]);
          }
          // Close the dialog
          setCollectionCreateDialogOpen(false);
          // Refresh collections list
          getCollections();
        } else {
          openSnackbar(parsedMessage.data?.message || t('collection.create_error') || 'Failed to create collection', 3000, 'error');
        }
      } else if (parsedMessage.command === "getAllCollections") {
        // Handle getAllCollections response from server
        console.log("Get all collections response:", parsedMessage);
        if (parsedMessage.data && parsedMessage.data.status === "success") {
          console.log("Collections received:", parsedMessage.data.collections);
          setCollections(parsedMessage.data.collections || []);
        } else {
          console.error('Error getting collections:', parsedMessage.data?.message);
          setCollections([]);
        }
        setCollectionsLoading(false);
      } else if (parsedMessage.command === "addProjectToCollection") {
        // Handle addProjectToCollection response from server
        console.log("Add project to collection response:", parsedMessage);
        if (parsedMessage.data && parsedMessage.data.status === "success") {
          openSnackbar(t('collection.case_added_success') || 'Case added to collection successfully', 3000, 'success');
          // Refresh the cases list to reflect the new collection assignment
          debouncedGetListOfCases(searchQuery, currentPage);
        } else {
          console.error('Error adding case to collection:', parsedMessage.data?.message);
          openSnackbar(t('collection.add_error') || 'Failed to add case to collection', 3000, 'error');
        }
      }

      if (parsedMessage.command === "searchcases") {
        console.log('Received searchcases response:', parsedMessage.data);
        setCases(parsedMessage.data.cases);
        setTotalCount(parsedMessage.data.total_count);
        setNewEventsCount(parsedMessage.data.new_events_count || 0);
        setIsLoading(false); // Clear loading state when we receive cases
        console.log('Cases updated:', parsedMessage.data.cases.length, 'cases');
      }
    });

    return unsubscribe;
  }, [subscribeToMessages, navigate, renamingCaseId, openSnackbar, t, getCollections, currentPage, debouncedGetListOfCases, searchQuery, handleDeleteResponse, caseToDelete?.id, isReadOnly, storageRestrictionEnabled, storageAllowedRoot, normalizedAllowedRoot, normalizePathForCompare, requestStorageDirectory, storageBrowserPath]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      if (parsedMessage.command !== 'openfile') {
        return;
      }
      const data = parsedMessage.data || {};
      if (!storageDownloadJob.active) {
        return;
      }

      const status = data.status || '';
      if (status === 'file_stream_start') {
        setStorageDownloadJob((prev) => ({
          ...prev,
          totalChunks: data.total_chunks || 0,
          receivedChunks: 0
        }));
        storageDownloadChunksRef.current = [];
      } else if (status === 'file_stream_chunk') {
        if (!data.chunk_data) return;
        try {
          const binaryString = atob(data.chunk_data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          storageDownloadChunksRef.current[data.chunk_number || 0] = bytes;
          setStorageDownloadJob((prev) => ({
            ...prev,
            receivedChunks: prev.receivedChunks + 1
          }));
        } catch (e) {
          console.error('Error processing download chunk', e);
        }
      } else if (status === 'file_stream_complete') {
        const validChunks = storageDownloadChunksRef.current.filter(Boolean);
        assembleAndSaveFile(storageDownloadJob.filename || data.filename, validChunks);
        storageDownloadChunksRef.current = [];
        setStorageDownloadJob({
          active: false,
          filename: '',
          totalChunks: 0,
          receivedChunks: 0
        });
      } else if (status === 'error') {
        setStorageDownloadJob({
          active: false,
          filename: '',
          totalChunks: 0,
          receivedChunks: 0
        });
        openSnackbar(data.message || t('sidebar.storage_download_error', 'Failed to download file'), 3000, 'error');
      }
    });

    return unsubscribe;
  }, [assembleAndSaveFile, openSnackbar, storageDownloadJob.active, storageDownloadJob.filename, subscribeToMessages, t]);

  useEffect(() => {
    if (currentCaseId) {
      setSelectedCaseId(currentCaseId);
    }
  }, [currentCaseId]);

  const isAllChangesCase = (caseData = {}) => {
    if (caseData.is_all_changes !== undefined) {
      return Boolean(caseData.is_all_changes);
    }
    return (caseData.title || '').trim().toLowerCase() === 'all changes';
  };

  const getTimestampValue = (value) => {
    if (!value) {
      return 0;
    }
    const normalizedValue = typeof value === 'string'
      ? value.replace(' ', 'T')
      : value;
    const date = new Date(normalizedValue);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  };

  const sortedCases = useMemo(() => {
    return [...cases].sort((a, b) => {
      const aIsAllChanges = isAllChangesCase(a);
      const bIsAllChanges = isAllChangesCase(b);

      if (aIsAllChanges !== bIsAllChanges) {
        return aIsAllChanges ? -1 : 1;
      }

      const aLastVisit = getTimestampValue(a?.last_visit || a?.created_at);
      const bLastVisit = getTimestampValue(b?.last_visit || b?.created_at);
      if (aLastVisit !== bLastVisit) {
        return bLastVisit - aLastVisit;
      }

      const aHasNewEvents = a?.hasNewEvents || 0;
      const bHasNewEvents = b?.hasNewEvents || 0;
      if (aHasNewEvents !== bHasNewEvents) {
        return bHasNewEvents - aHasNewEvents;
      }

      return (b?.id || 0) - (a?.id || 0);
    });
  }, [cases]);

  useEffect(() => {
    debouncedGetListOfCases(searchQuery, currentPage);
  }, [searchQuery, currentPage, debouncedGetListOfCases]);

  const handleListItemClick = useCallback((id) => {
    setSelectedCaseId(id);

    setCases(prevCases =>
      prevCases.map(caseData =>
        caseData.id === id ? { ...caseData, hasNewEvents: 0 } : caseData
      )
    );

    setNewEventsCount(prevCount => {
      const selectedCase = cases.find(c => c.id === id);
      return selectedCase && selectedCase.hasNewEvents === 1
        ? prevCount - 1
        : prevCount;
    });

    // Trigger configuration check when a case is clicked
    if (sendMessage && isConnected) {
      sendMessage({ command: 'configuration_check' });
    }
  }, [cases, sendMessage, isConnected]);

  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;

  return (
    <>
      {/* Main Sidebar Drawer */}
      <SidebarDrawer
        isOpen={isOpen}
        onToggle={toggleDrawer}
        cases={sortedCases}
        collections={collections}
        collectionsLoading={collectionsLoading}
        selectedCollection={selectedCollection}
        onCollectionChange={handleCollectionChange}
        selectedCaseId={selectedCaseId}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onCaseClick={handleListItemClick}
        onCaseMenuOpen={handleCaseMenuOpen}
        onLanguageMenuOpen={handleLanguageMenuOpen}
        onLogout={logout}
        isUserLoggedIn={isUserLoggedIn}
        isLoading={isLoading}
        connectionError={connectionError}
        onRetry={retryConnection}
        onCreateProject={() => navigate('/add')}
        onCreateCollection={handleCollectionCreateDialogOpen}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
        selectedColor={selectedColor}
        lightPurple={lightPurple}
        storageStatus={storageStatus}
        credits={credits}
        restrictToUserFolder={storageRestrictionEnabled}
        storageDisplayPath={storageDisplayPath}
        storageFolders={storageFolders}
        storageFiles={storageFiles}
        storageBrowserLoading={storageBrowserLoading}
        storageBrowserError={storageBrowserError}
        onStorageFolderClick={handleStorageFolderClick}
        onStorageNavigateUp={handleStorageNavigateUp}
        onStorageRefresh={handleStorageRefresh}
        onStorageOpen={openStorageDialog}
      />

      {/* Sidebar Toggle Button */}
      <SidebarToggleButton
        onClick={toggleDrawer}
        isOpen={isOpen}
        newEventsCount={newEventsCount}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
      />

      {/* Language Menu */}
      <SidebarLanguageMenu
        anchorEl={languageMenuAnchorEl}
        open={Boolean(languageMenuAnchorEl)}
        onClose={handleLanguageMenuClose}
        onLanguageChange={changeLanguage}
        primaryColor={primaryColor}
        lightPurple={lightPurple}
      />

      {/* Case Context Menu */}
      <CaseContextMenu
        anchorEl={caseMenuAnchorEl}
        open={Boolean(caseMenuAnchorEl)}
        onClose={handleCaseMenuClose}
        onRename={handleRenameClick}
        onCopy={handleCopyClick}
        onSaveAsTemplate={handleSaveAsTemplateClick}
        onAddToCollection={() => handleAddToCollection(selectedCaseForMenu)}
        onDelete={handleDeleteClick}
        isDeleting={isDeleting}
        primaryColor={primaryColor}
        lightPurple={lightPurple}
      />

      {/* Rename Dialog */}
      <CaseRenameDialog
        open={renameDialogOpen}
        onClose={handleRenameDialogClose}
        currentTitle={renamingCase?.title || ''}
        onRename={handleRenameSubmit}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
      />

      {/* Copy Dialog */}
      <CaseDuplicateDialog
        open={copyDialogOpen}
        onClose={handleCopyDialogClose}
        originalTitle={copyingCase?.title || ''}
        onDuplicate={handleCopySubmit}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
        collections={collections}
        defaultCollectionId={selectedCollection}
      />

      {/* Save Template Dialog */}
      <SaveTemplateDialog
        open={saveTemplateDialogOpen}
        onClose={handleSaveTemplateDialogClose}
        onSave={handleSaveTemplateSubmit}
        caseData={templateCase}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
      />

      {/* Collection Create Dialog */}
      <CollectionCreateDialog
        open={collectionCreateDialogOpen}
        onClose={handleCollectionCreateDialogClose}
        onCreate={handleCollectionCreate}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
      />

      {/* Add to Collection Dialog */}
      <AddToCollectionDialog
        open={addToCollectionDialogOpen}
        onClose={handleAddToCollectionCancel}
        onConfirm={handleAddToCollectionConfirm}
        onCancel={handleAddToCollectionCancel}
        collections={collections}
        caseTitle={caseToAddToCollection?.title}
        primaryColor={primaryColor}
        lightPurple={lightPurple}
      />

      {/* Delete Confirmation Dialog - Using reusable component */}
      <CaseDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        caseTitle={caseToDelete?.title}
      />

      {/* Overlays */}
      {certOverlayOpen && (
        <CertificationExpiredOverlay
          open={certOverlayOpen}
          onClose={() => setCertOverlayOpen(false)}
        />
      )}
      {softwareOutdatedOverlayOpen && (
        <SoftwareOutdatedOverlay
          open={softwareOutdatedOverlayOpen}
          onClose={() => setSoftwareOutdatedOverlayOpen(false)}
          downloadLink={downloadLink}
          hideDownload={isRestrictedFolderMode}
        />
      )}

      <StorageBrowser
        open={storageDialogOpen}
        onClose={closeStorageDialog}
        displayPath={storageDisplayPath}
        folders={storageFolders}
        files={storageFiles}
        loading={storageBrowserLoading}
        error={storageBrowserError}
        onFolderClick={handleStorageFolderClick}
        onNavigateUp={handleStorageNavigateUp}
        onRefresh={handleStorageRefresh}
        onCreateFolder={handleStorageCreateFolder}
        onDeleteFile={handleStorageDeleteFile}
        onDeleteFolder={handleStorageDeleteFolder}
        onDownload={handleStorageFileDownload}
        onDownloadAll={handleStorageFolderDownload}
        currentPath={storageBrowserPath || storageAllowedRoot || ''}
        canManageStorage={canManageStorage}
        primaryColor={primaryColor}
        downloadingFolder={storageDownloadJob.preparing || false}
        restrictToUserFolder={storageRestrictionEnabled}
      />
    </>
  );
};

export default Sidebar;
